import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  EditOutlined,
  PlusOutlined,
  StopOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import config from "../../api_config.json";
import { useLocation } from "react-router-dom";

const { Title } = Typography;
const { TextArea } = Input;

const emptyCoupon = {
  code: "",
  name: "",
  status: "active",
  benefitType: "plus_lifetime",
  plan: "plus",
  planStatus: "coupon_lifetime",
  sponsorMode: "ofc",
  publicMessage: "",
  internalNote: "",
  expiresAt: "",
  maxRedemptions: null,
};

const couponStatusColors = {
  active: "green",
  inactive: "orange",
  expired: "red",
};

const sponsorLabels = {
  ofc: "Solo OFC",
  ofc_af: "OFC + AF",
};

const benefitLabels = {
  plus_lifetime: "Plus gratuito",
  discount: "Sconto",
  custom: "Custom",
};

const Coupons = () => {
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";
  const { getTokenSilently } = useAuth0();
  const location = useLocation();
  const [form] = Form.useForm();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const couponQuery = new URLSearchParams(location.search)
    .get("coupon")
    ?.trim()
    .toUpperCase();
  const visibleCoupons = useMemo(() => {
    if (!couponQuery) {
      return coupons;
    }

    return coupons.filter((coupon) => coupon.code === couponQuery);
  }, [couponQuery, coupons]);

  const apiRequest = useCallback(
    async (path, options = {}) => {
      const token = await getTokenSilently();
      const response = await fetch(`${config.baseUrl[env]}${path}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...(options.headers || {}),
        },
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Operazione coupon non riuscita");
      }

      return data;
    },
    [env, getTokenSilently],
  );

  const fetchCoupons = useCallback(async () => {
    setLoading(true);

    try {
      const data = await apiRequest("/admin/coupons");
      setCoupons(data.data || []);
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiRequest]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  const openCreateModal = () => {
    setEditingCoupon(null);
    form.setFieldsValue(emptyCoupon);
    setModalOpen(true);
  };

  const openEditModal = (coupon) => {
    setEditingCoupon(coupon);
    form.setFieldsValue({
      ...emptyCoupon,
      ...coupon,
      maxRedemptions: coupon.maxRedemptions || null,
    });
    setModalOpen(true);
  };

  const saveCoupon = async () => {
    const values = await form.validateFields();
    const coupon = {
      ...values,
      code: values.code?.trim().toUpperCase(),
      maxRedemptions: values.maxRedemptions || null,
    };

    setSaving(true);

    try {
      if (editingCoupon) {
        const { code, ...patch } = coupon;
        await apiRequest(`/admin/coupons/${encodeURIComponent(code)}`, {
          method: "PATCH",
          body: JSON.stringify({ coupon: patch }),
        });
        message.success("Coupon aggiornato.");
      } else {
        await apiRequest("/admin/coupons", {
          method: "POST",
          body: JSON.stringify({ coupon }),
        });
        message.success("Coupon creato.");
      }

      setModalOpen(false);
      await fetchCoupons();
    } catch (err) {
      message.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleCouponStatus = async (coupon) => {
    const nextStatus = coupon.status === "active" ? "inactive" : "active";

    try {
      await apiRequest(`/admin/coupons/${encodeURIComponent(coupon.code)}`, {
        method: "PATCH",
        body: JSON.stringify({
          coupon: {
            status: nextStatus,
          },
        }),
      });
      message.success(
        nextStatus === "active" ? "Coupon attivato." : "Coupon disattivato.",
      );
      await fetchCoupons();
    } catch (err) {
      message.error(err.message);
    }
  };

  const columns = [
    {
      title: "Codice",
      dataIndex: "code",
      key: "code",
      render: (code) => <strong>{code}</strong>,
    },
    {
      title: "Nome",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Stato",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={couponStatusColors[status] || "default"}>{status}</Tag>
      ),
      filters: [
        { text: "Active", value: "active" },
        { text: "Inactive", value: "inactive" },
        { text: "Expired", value: "expired" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Benefit",
      dataIndex: "benefitType",
      key: "benefitType",
      render: (value) => benefitLabels[value] || value,
    },
    {
      title: "Piano",
      key: "plan",
      render: (_, record) => `${record.plan || "-"} · ${record.planStatus || "-"}`,
    },
    {
      title: "Sponsor",
      dataIndex: "sponsorMode",
      key: "sponsorMode",
      render: (value) => sponsorLabels[value] || value,
    },
    {
      title: "Riscatti",
      key: "redemptions",
      render: (_, record) =>
        `${record.redeemedCount || 0}${
          record.maxRedemptions ? ` / ${record.maxRedemptions}` : ""
        }`,
    },
    {
      title: "Scadenza",
      dataIndex: "expiresAt",
      key: "expiresAt",
      render: (value) => value || "-",
    },
    {
      title: "Azioni",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
          >
            Modifica
          </Button>
          <Button
            size="small"
            icon={
              record.status === "active" ? (
                <StopOutlined />
              ) : (
                <CheckCircleOutlined />
              )
            }
            danger={record.status === "active"}
            onClick={() => toggleCouponStatus(record)}
          >
            {record.status === "active" ? "Disattiva" : "Attiva"}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 24 }}>
        <Space
          style={{ width: "100%", justifyContent: "space-between" }}
          align="center"
        >
          <Title level={3} style={{ margin: 0 }}>
            Coupon
          </Title>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            Nuovo coupon
          </Button>
        </Space>
      </div>

      <Table
        rowKey="code"
        loading={loading}
        columns={columns}
        dataSource={visibleCoupons}
        expandable={{
          expandedRowRender: (record) => (
            <div>
              {record.publicMessage && (
                <p>
                  <strong>Messaggio pubblico:</strong> {record.publicMessage}
                </p>
              )}
              {record.internalNote && (
                <p>
                  <strong>Nota interna:</strong> {record.internalNote}
                </p>
              )}
              <p>
                <strong>Creato:</strong> {record.createdAt || "-"} ·{" "}
                <strong>Aggiornato:</strong> {record.updatedAt || "-"}
              </p>
            </div>
          ),
        }}
      />

      <Modal
        visible={modalOpen}
        title={editingCoupon ? "Modifica coupon" : "Nuovo coupon"}
        okText={editingCoupon ? "Salva" : "Crea"}
        cancelText="Annulla"
        confirmLoading={saving}
        onCancel={() => setModalOpen(false)}
        onOk={saveCoupon}
        width={760}
      >
        <Form form={form} layout="vertical" initialValues={emptyCoupon}>
          <Form.Item
            name="code"
            label="Codice"
            rules={[{ required: true, message: "Codice obbligatorio" }]}
          >
            <Input disabled={Boolean(editingCoupon)} />
          </Form.Item>
          <Form.Item name="name" label="Nome interno">
            <Input />
          </Form.Item>
          <Space style={{ width: "100%" }} size="large" align="start">
            <Form.Item name="status" label="Stato" style={{ minWidth: 160 }}>
              <Select>
                <Select.Option value="active">Attivo</Select.Option>
                <Select.Option value="inactive">Disattivo</Select.Option>
                <Select.Option value="expired">Scaduto</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="benefitType"
              label="Benefit"
              style={{ minWidth: 190 }}
            >
              <Select>
                <Select.Option value="plus_lifetime">
                  Plus gratuito
                </Select.Option>
                <Select.Option value="discount">Sconto</Select.Option>
                <Select.Option value="custom">Custom</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="sponsorMode"
              label="Sponsor"
              style={{ minWidth: 190 }}
            >
              <Select>
                <Select.Option value="ofc">Solo OFC</Select.Option>
                <Select.Option value="ofc_af">OFC + AF</Select.Option>
              </Select>
            </Form.Item>
          </Space>
          <Space style={{ width: "100%" }} size="large" align="start">
            <Form.Item name="plan" label="Piano" style={{ minWidth: 160 }}>
              <Select>
                <Select.Option value="gratis">Gratis</Select.Option>
                <Select.Option value="plus">Plus</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="planStatus"
              label="Stato piano"
              style={{ minWidth: 190 }}
            >
              <Select>
                <Select.Option value="active">Attivo</Select.Option>
                <Select.Option value="coupon_lifetime">
                  Coupon lifetime
                </Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="maxRedemptions"
              label="Limite riscatti"
              style={{ minWidth: 190 }}
            >
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>
          </Space>
          <Form.Item
            name="expiresAt"
            label="Scadenza"
            extra="Formato ISO consigliato, es. 2026-12-31T23:59:59.000Z. Lascia vuoto per nessuna scadenza."
          >
            <Input />
          </Form.Item>
          <Form.Item name="publicMessage" label="Messaggio pubblico">
            <TextArea rows={3} maxLength={500} showCount />
          </Form.Item>
          <Form.Item name="internalNote" label="Nota interna">
            <TextArea rows={3} maxLength={1000} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default Coupons;
